import { useEffect } from 'react'
import Swal from 'sweetalert2'

import useMembershipsData from '@/hooks/useMembershipsData'
import { useDateRangeFilter } from './filters/DateRangeFilter'

import { useMembershipSelection } from './hooks/useMembershipSelection'
import { useMembershipActions } from './hooks/useMembershipActions'

import SearchActionsBar from '@/widgets/SearchActionsBar'
import MembershipsTable from './components/MembershipsTable'
import LoadMoreSection from '@/widgets/LoadMoreSection'

export default function Memberships() {
	const {
		data: membershipsData,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData: loadMembershipsData,
		handleLoadMore,
		setError,
		setSorting: setMembershipsSorting,
		handleSearch,
		sortColumn,
		sortDirection,
		searchQuery,
		updateFilters,
	} = useMembershipsData()

	const { selectedMemberships, handleSelectAll, handleSelectMembership, clearSelection, getSelectionState } =
		useMembershipSelection()

	const { isDeleting, handleDeleteSelected } = useMembershipActions(loadMembershipsData)

	const { fromDate, toDate, handleDateRangeFilter, getDateRangeDisplay } = useDateRangeFilter(updateFilters)

	useEffect(() => {
		if (error) {
			Swal.fire({ title: String(error), icon: 'error' }).then(() => setError())
		}
	}, [error, setError])

	useEffect(() => {
		loadMembershipsData()
	}, [loadMembershipsData])

	const handleSort = column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setMembershipsSorting(column, newDirection)
	}

	const handleSearchChange = e => {
		handleSearch(e.target.value)
	}

	const { isAllSelected, isIndeterminate } = getSelectionState(membershipsData)

	const onDeleteSelected = () => {
		handleDeleteSelected(selectedMemberships, clearSelection)
	}

	const onSelectAll = checked => {
		handleSelectAll(checked, membershipsData)
	}

	const buttons = [
		{
			type: 'delete',
			label: 'Delete',
			count: selectedMemberships.size,
			onClick: onDeleteSelected,
			loading: isDeleting,
		},
		{
			type: 'date',
			label: 'Date range',
			active: !!(fromDate || toDate),
			onClick: handleDateRangeFilter,
			title: getDateRangeDisplay() || 'Filter by date range',
		},
		{
			type: 'add',
			label: 'Add new',
			to: '/memberships/add',
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder="Search memberships..."
				buttons={buttons}
			/>

			<MembershipsTable
				membershipsData={membershipsData}
				loading={loading}
				selectedMemberships={selectedMemberships}
				onSelectAll={onSelectAll}
				onSelectMembership={handleSelectMembership}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				onSort={handleSort}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={membershipsData.length}
				totalCount={totalCount}
			/>
		</>
	)
}
